/**
 * Chat drawer — Support Hub (5000-style) + Shopify Inbox live view.
 */
(function () {
  const DRAWER_ID = 'chat-drawer';
  const VIEW_ATTR = 'data-uucck-chat-view';

  const GUIDANCE = {
    'Product Questions':
      'Include the product name or SKU, your application, battery voltage, and required quantity.',
    'Order Status':
      'Include your order number and the email you used at checkout. Never send passwords or payment-card details.',
    Wholesale:
      'Use the Wholesale topic to email our sales team with product, quantity, and destination country.',
    'Technical Support':
      'Include the product or SKU, your current components, battery voltage, and the issue you are troubleshooting.',
  };

  /** @type {HTMLElement | null} */
  let shell = null;
  /** @type {HTMLElement | null} */
  let hubView = null;
  /** @type {HTMLElement | null} */
  let liveView = null;
  /** @type {HTMLElement | null} */
  let backBtn = null;
  /** @type {HTMLElement | null} */
  let subtitleEl = null;
  /** @type {HTMLElement | null} */
  let liveHint = null;

  function getDrawer() {
    return document.getElementById(DRAWER_ID);
  }

  function getShopifyChat() {
    return document.querySelector('shopify-chat');
  }

  function getTopic() {
    const active = hubView?.querySelector('.uucck-support-hub__topic.is-active');
    return active?.dataset.uucckSupportTopic || 'Product Questions';
  }

  function syncTopicUi() {
    if (!hubView) return;
    const topic = getTopic();
    const guidance = hubView.querySelector('[data-uucck-support-guidance]');
    if (guidance && GUIDANCE[topic]) {
      guidance.textContent = GUIDANCE[topic];
    }
  }

  function setView(view) {
    if (!shell) return;
    const isLive = view === 'live';
    shell.setAttribute(VIEW_ATTR, isLive ? 'live' : 'hub');
    hubView?.toggleAttribute('hidden', isLive);
    liveView?.toggleAttribute('hidden', !isLive);
    backBtn?.toggleAttribute('hidden', !isLive);
    subtitleEl?.toggleAttribute('hidden', isLive);

    const chat = getShopifyChat();
    const drawer = getDrawer();

    if (isLive) {
      if (chat) {
        chat.setAttribute('mode', 'managed');
        chat.setAttribute('open', '');
      } else {
        liveHint?.classList.add('is-unavailable');
      }
    } else if (chat?.getAttribute('mode') === 'managed') {
      chat.removeAttribute('open');
      chat.setAttribute('mode', 'standalone');
    }

    if (drawer && typeof drawer.open === 'function' && !drawer.hasAttribute('open') && isLive) {
      drawer.open();
    }
  }

  function showHub() {
    setView('hub');
  }

  function showLive() {
    const chat = getShopifyChat();
    const inboxReady = Boolean(customElements.get('shopify-chat') && chat);
    const fallback = liveView?.querySelector('[data-uucck-inbox-fallback]');

    if (!inboxReady) {
      liveHint?.classList.add('is-unavailable');
      if (liveHint) {
        liveHint.textContent =
          'Live chat needs Shopify Inbox. Use WhatsApp or the email form meanwhile.';
      }
      fallback?.removeAttribute('hidden');
    } else {
      liveHint?.classList.remove('is-unavailable');
      fallback?.setAttribute('hidden', '');
    }

    setView('live');
  }

  function bindHub() {
    if (!hubView || hubView.dataset.uucckHubBound === 'true') return;
    hubView.dataset.uucckHubBound = 'true';

    hubView.querySelectorAll('[data-uucck-support-topic]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.hasAttribute('data-uucck-support-wholesale')) {
          window.location.href =
            'mailto:admin@uucck.com?subject=' + encodeURIComponent('Wholesale quote request');
          return;
        }
        hubView.querySelectorAll('.uucck-support-hub__topic').forEach((chip) => {
          const on = chip === btn;
          chip.classList.toggle('is-active', on);
          chip.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        syncTopicUi();
      });
    });

    hubView.querySelector('[data-uucck-chat-open-live]')?.addEventListener('click', () => {
      showLive();
    });

    const form = hubView.querySelector('.uucck-support-hub__form');
    form?.addEventListener('submit', () => {
      const body = hubView.querySelector('[data-uucck-support-body]');
      if (!body || body.dataset.uucckTopicPrefixed === 'true') return;
      const topic = getTopic();
      const trimmed = body.value.trim();
      body.value = `[${topic}]\n\n${trimmed}`;
      body.dataset.uucckTopicPrefixed = 'true';
    });

    syncTopicUi();
  }

  function bindShell() {
    shell = document.querySelector('.uucck-chat-drawer__shell');
    if (!shell) return;
    hubView = shell.querySelector('[data-uucck-chat-pane="hub"]');
    liveView = shell.querySelector('[data-uucck-chat-pane="live"]');
    backBtn = shell.querySelector('[data-uucck-chat-back]');
    subtitleEl = shell.querySelector('[data-uucck-chat-subtitle]');
    liveHint = shell.querySelector('[data-uucck-chat-live-hint]');

    bindHub();

    shell.querySelectorAll('[data-uucck-chat-back]').forEach((btn) => {
      btn.addEventListener('click', () => showHub());
    });

    document.addEventListener('uucck-chat:open', () => showHub());
    document.addEventListener('theme-drawer:open', (event) => {
      if (/** @type {Element} */ (event.target).id !== DRAWER_ID) return;
      showHub();
    });
    document.addEventListener('theme-drawer:close', (event) => {
      if (/** @type {Element} */ (event.target).id !== DRAWER_ID) return;
      showHub();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindShell);
  } else {
    bindShell();
  }

  customElements.whenDefined('shopify-chat').then(() => {
    liveHint?.classList.remove('is-unavailable');
  }).catch(() => {});

  window.uucckChatDrawer = { showHub, showLive };
})();
